import { ApiError, requestJSON } from "./transport";
export { ApiError } from "./transport";
import {
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryClient, SessionContext } from "./session-state";
export { queryClient } from "./session-state";

export type Row = { id: string; [key: string]: any };
export type Account = Row & {
  name: string;
  username: string;
  role: string;
  schoolName?: string;
  tenantId?: string;
};
export const TOKEN_KEY = "teaching-flow-teacher-token";
function clearDrafts() {
  for (const key of Object.keys(sessionStorage))
    if (
      key.startsWith("huiju-next-code:") ||
      key.startsWith("huiju-coding-pending:teacher:")
    )
      sessionStorage.removeItem(key);
}
export const API = import.meta.env.VITE_API_BASE || "/api";
export const DEMO = import.meta.env.VITE_DEMO === "true";
export function tenantSlug(host: string) {
  const suffix =
    import.meta.env.VITE_TENANT_DOMAIN_SUFFIX || "hjjy.hjoi.com.cn";
  return host.endsWith(`.${suffix}`) ? host.slice(0, -suffix.length - 1) : "";
}
export const rawRequest = <T,>(
  path: string,
  token: string,
  options: RequestInit = {},
) => requestJSON<T>(API + path, token, options);
export function SessionProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState(
    () =>
      sessionStorage.getItem(TOKEN_KEY) ||
      localStorage.getItem(TOKEN_KEY) ||
      "",
  );
  const currentToken = useRef(token);
  const [account, setAccount] = useState<Account | null>(null);
  const clear = useCallback(() => {
    currentToken.current = "";
    queryClient.cancelQueries();
    queryClient.clear();
    clearDrafts();
    setAccount(null);
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);
  const login = useCallback((next: string) => {
    currentToken.current = next;
    queryClient.cancelQueries();
    queryClient.clear();
    clearDrafts();
    setAccount(null);
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.setItem(TOKEN_KEY, next);
    setToken(next);
  }, []);
  const request = useCallback(
    async <T,>(path: string, options?: RequestInit) => {
      try {
        if (currentToken.current !== token)
          throw new ApiError("账号已切换，请重新操作", 499);
        const result = await rawRequest<T>(path, token, options);
        if (currentToken.current !== token)
          throw new ApiError("账号已切换，请重新操作", 499);
        return result;
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.status === 401 &&
          currentToken.current === token &&
          !path.startsWith("/auth/")
        )
          clear();
        throw error;
      }
    },
    [token, clear],
  );
  return (
    <SessionContext.Provider
      value={{
        token,
        account,
        setAccount,
        login,
        logout: clear,
        request,
        scope: `${account?.tenantId || location.hostname}:${account?.role || ""}:${account?.id || "pending"}`,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
export const useSession = () => useContext(SessionContext);
export function useApi<T = Row[]>(path: string, enabled = true) {
  const { request, token, scope } = useSession();
  return useQuery({
    queryKey: [scope, path],
    queryFn: ({ signal }) => request<T>(path, { signal }),
    enabled: !!token && enabled,
  });
}
export function useActions() {
  const { request } = useSession();
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const lock = useRef(false);
  const run = async (work: () => Promise<unknown>) => {
    if (lock.current) return false;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      await work();
      await client.invalidateQueries();
      return true;
    } catch (e) {
      const message = e instanceof Error ? e.message : "操作失败";
      setError(message);
      return false;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return {
    busy,
    error,
    setError,
    run,
    request,
    mutate: (path: string, method: string, body?: unknown) =>
      run(() =>
        request(path, {
          method,
          ...(body === undefined
            ? {}
            : { body: body instanceof FormData ? body : JSON.stringify(body) }),
        }),
      ),
  };
}
export const enc = encodeURIComponent;
export function items(value: any): Row[] {
  return Array.isArray(value) ? value : value?.items || [];
}
export function options(rows: Row[]) {
  return rows.map((r) => ({ value: r.id, label: r.name || r.title || r.id }));
}
export function asBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(new Error("无法读取文件"));
    reader.readAsDataURL(file);
  });
}
export async function download(
  path: string,
  token: string,
  name: string,
  preview = false,
) {
  const response = await fetch(API + path, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok)
    throw new Error(
      (await response.json().catch(() => ({}))).message || "文件读取失败",
    );
  const url = URL.createObjectURL(await response.blob());
  if (preview) window.open(url, "_blank", "noopener,noreferrer");
  else {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
